/**
 * Roguelite Modifier Registry — ModifierDef interface + Phase 1 modifiers.
 */

import type { WeaponDef } from '../weapons/types';
import type { GameState, ModeState } from '../types';

export interface ModifierDef {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic';
  category: 'projectile' | 'explosion' | 'knockback' | 'utility';
  fieldMutations?: (weapon: WeaponDef) => Partial<WeaponDef>;
  wrapOnImpact?: (original: WeaponDef['onImpact']) => WeaponDef['onImpact'];
  wrapOnBounce?: (original: WeaponDef['onBounce']) => WeaponDef['onBounce'];
  requires?: (weapon: WeaponDef | null, allMods: string[]) => boolean;
  excludes?: string[];
  immediateEffect?: (state: GameState) => void;
  minZone?: number;
  isUtility?: boolean;
}

const MODIFIER_REGISTRY: Record<string, ModifierDef> = {};

function register(mod: ModifierDef): void {
  MODIFIER_REGISTRY[mod.id] = mod;
}

export function lookupModifier(id: string): ModifierDef | null {
  return MODIFIER_REGISTRY[id] ?? null;
}

/** Get modifiers eligible for the current run state + zone. */
export function getEligibleModifiers(ms: ModeState, zone: number): ModifierDef[] {
  const allMods = [...ms.rogueliteShot1Mods, ...ms.rogueliteShot2Mods];
  return Object.values(MODIFIER_REGISTRY).filter(mod => {
    if (mod.minZone && zone < mod.minZone) return false;
    if (mod.excludes) {
      for (const ex of mod.excludes) {
        if (allMods.includes(ex)) return false;
      }
    }
    if (mod.requires) {
      // For utility mods, pass null weapon
      if (!mod.requires(null, allMods)) return false;
    }
    return true;
  });
}

// ─── Phase 1 Modifiers (10) ────────────────────────────────────────────

register({
  id: 'ricochet',
  name: 'Ricochet',
  description: '+2 bounces, 75% energy retention',
  rarity: 'common',
  category: 'projectile',
  fieldMutations: (w) => ({
    bounces: w.bounces + 2,
    bounceEnergyRetention: 0.75,
  }),
});

register({
  id: 'big_boom',
  name: 'Big Boom',
  description: '+40% blast radius, -15% damage',
  rarity: 'common',
  category: 'explosion',
  fieldMutations: (w) => ({
    blastRadius: w.blastRadius * 1.4,
    damage: w.damage * 0.85,
  }),
});

register({
  id: 'concentrated',
  name: 'Concentrated',
  description: '-35% blast, +35% damage, +15% knockback',
  rarity: 'common',
  category: 'explosion',
  fieldMutations: (w) => ({
    blastRadius: w.blastRadius * 0.65,
    damage: w.damage * 1.35,
    knockbackMult: w.knockbackMult * 1.15,
  }),
});

register({
  id: 'launcher',
  name: 'Launcher',
  description: '+40% knockback force',
  rarity: 'common',
  category: 'knockback',
  fieldMutations: (w) => ({
    knockbackMult: w.knockbackMult * 1.4,
  }),
});

register({
  id: 'scatter',
  name: 'Scatter',
  description: 'Impact spawns 3 pellets in 120° cone',
  rarity: 'uncommon',
  category: 'projectile',
  // Hook-based: wraps onImpact (Phase 2 implementation, field mutation placeholder for now)
  fieldMutations: () => ({
    // Scatter effect applied via hook wrapper — no field mutations
  }),
});

register({
  id: 'grapple_hook',
  name: 'Grapple Hook',
  description: 'Unlocks grapple hook',
  rarity: 'common',
  category: 'utility',
  isUtility: true,
  minZone: 3,
  immediateEffect: (state: GameState) => {
    const ms = state.modeState;
    ms.rogueliteHasGrapple = true;
    // Immediately grant grapple ammo
    if (state.grapple.ammo.length > 0) {
      state.grapple.ammo[0] = 3 + ms.rogueliteGrappleBonus;
    }
  },
  requires: (_w, allMods) => !allMods.includes('grapple_hook'),
});

register({
  id: 'grapple_plus',
  name: 'Grapple+',
  description: '+1 grapple ammo per round',
  rarity: 'common',
  category: 'utility',
  isUtility: true,
  immediateEffect: (state: GameState) => {
    state.modeState.rogueliteGrappleBonus++;
    if (state.modeState.rogueliteHasGrapple && state.grapple.ammo.length > 0) {
      state.grapple.ammo[0] = 3 + state.modeState.rogueliteGrappleBonus;
    }
  },
  requires: (_w, allMods) => allMods.includes('grapple_hook') || false,
});

register({
  id: 'momentum_plus',
  name: 'Momentum+',
  description: '+30 base momentum per turn',
  rarity: 'common',
  category: 'utility',
  isUtility: true,
  immediateEffect: (state: GameState) => {
    state.modeState.rogueliteMomentumBonus += 30;
  },
});

register({
  id: 'quick_draw',
  name: 'Quick Draw',
  description: '40% faster charge rate',
  rarity: 'uncommon',
  category: 'utility',
  isUtility: true,
  immediateEffect: (state: GameState) => {
    state.modeState.rogueliteChargeRateMult *= 1.4;
  },
});

register({
  id: 'healing',
  name: 'Healing',
  description: '-20% damage (min 0)',
  rarity: 'common',
  category: 'utility',
  isUtility: true,
  immediateEffect: (state: GameState) => {
    const human = state.players[0];
    if (human) {
      human.damagePercent = Math.max(0, human.damagePercent - 20);
    }
  },
});
